import { Formik } from 'formik';
import { useCallback, useContext } from 'react';

import { customersApi } from 'src/app/services/Customers/Customers.service';
import type { ICustomerLeadProductFrequencyTabFormValues } from 'src/components/customers/CustomerLeadTypes';
import {
  customerLeadFrequencyTabValidationSchema,
  toCustomerLeadFrequencyTabInitialValues,
} from 'src/components/customers/steps/Quantities/CustomerLeadStepFrequencyTab.validation';
import { CustomerLeadStepFrequencyTabContent } from 'src/components/customers/steps/Quantities/CustomerLeadStepFrequencyTabContent';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';

type CustomerLeadStepFrequencyTabProps = {
  customerProductLeadId: string;

  onSubmit: () => boolean;
};

export function CustomerLeadStepFrequencyTab({
  customerProductLeadId,
  onSubmit,
}: Readonly<CustomerLeadStepFrequencyTabProps>) {
  const { customerLeadProduct, updateCustomerLeadProduct } = useContext(CustomersContext);

  const [, partialUpdateCustomerLeadProduct] =
    customersApi.usePartialUpdateCustomerProductLeadMutation(customerProductLeadId);

  const save = useCallback(
    async (newValues: ICustomerLeadProductFrequencyTabFormValues, submit: boolean) => {
      const updatedCustomerProductLeadData = await partialUpdateCustomerLeadProduct({
        data: {
          frequency: {
            purchaseFrequency: newValues.purchaseFrequency,
            purchaseFrequencyQty: newValues.purchaseFrequencyQty,
            contractPricing: newValues.contractPricing,
            additionalInfo: newValues.additionalInfo,

            submit,
          },
        },
      });

      updateCustomerLeadProduct(updatedCustomerProductLeadData.data);
    },
    [partialUpdateCustomerLeadProduct, updateCustomerLeadProduct]
  );

  const handleSubmit = useCallback(
    async (submitValues: ICustomerLeadProductFrequencyTabFormValues) => {
      await save(submitValues, true);

      return onSubmit();
    },
    [save, onSubmit]
  );

  const handleChange = useCallback(
    (newValues: ICustomerLeadProductFrequencyTabFormValues) => save(newValues, false),
    [save]
  );

  if (!customerLeadProduct) {
    return null;
  }

  return (
    <Formik<ICustomerLeadProductFrequencyTabFormValues>
      initialValues={toCustomerLeadFrequencyTabInitialValues(customerLeadProduct)}
      validationSchema={customerLeadFrequencyTabValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formikProps) => <CustomerLeadStepFrequencyTabContent formikProps={formikProps} onChange={handleChange} />}
    </Formik>
  );
}
